import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogTitle } from '@radix-ui/react-dialog';

import { ArrowLeftIcon } from '@/assets/icons';
import { Button, Spinner } from '@/components/atoms';
import { TranslationOverviewCategory } from '@/components/molecules/TranslationOverviewCategory/TranslationOverviewCategory';
import { CATEGORY_POPUP_TEST_IDS } from '@/constants/data-testids';
import { TranslationCategory } from '@/types/management/managementCategories';
import { captureAsyncError } from '@/utils/captureAsyncError';

import { SetCategoryPopupStep } from '../CategoryPopup';

type TranslationOverviewCategoryPopupProps = {
  translationOptions: TranslationCategory[];
  isLoadingSubmitCategory?: boolean;
  submitButtonLabel: string;
  onEditClick: (option: TranslationCategory) => void;
  setStep: SetCategoryPopupStep;
  onAddCategory: () => Promise<void>;
};

export const TranslationOverviewCategoryPopup: FC<TranslationOverviewCategoryPopupProps> = ({
  translationOptions,
  isLoadingSubmitCategory,
  submitButtonLabel,
  onEditClick,
  onAddCategory,
  setStep,
}) => {
  const { t } = useTranslation('components');
  return (
    <div data-testid={CATEGORY_POPUP_TEST_IDS.STEP2}>
      <div className="mb-5">
        <DialogTitle className="text-body-12-semibold text-custom-warm-grey-500 mb-2">
          {t('translationOverviewCategory.title')}
        </DialogTitle>
        <h4 className="text-body-20-semibold text-custom-warm-grey-800 mb-1.5">
          {t('translationOverviewCategory.description')}
        </h4>
        <p className="text-body-14-regular text-custom-warm-grey-500">
          {t('translationOverviewCategory.subtitle')}
        </p>
      </div>
      <TranslationOverviewCategory
        translationOptions={translationOptions}
        className="flex flex-col gap-5 max-h-[576px] mb-6 overflow-y-auto pr-6 pb-1"
        onEditClick={onEditClick}
      />

      <div className="flex items-center gap-3">
        <Button
          size="lg"
          variant="secondary"
          className="w-full"
          onClick={() => setStep('step1')}
          leftIcon={<ArrowLeftIcon width={20} height={20} className="text-custom-warm-grey-500" />}
        >
          {t('translationOverviewCategory.buttons.backButton')}
        </Button>
        <Button
          size="lg"
          variant="primary"
          className="relative w-full"
          onClick={() => {
            void Promise.resolve(onAddCategory()).catch(captureAsyncError);
          }}
        >
          {submitButtonLabel}{' '}
          {isLoadingSubmitCategory && (
            <Spinner className="absolute top-1/2 -translate-y-1/2 right-8" />
          )}
        </Button>
      </div>
    </div>
  );
};
